import { Link } from "react-router-dom";
import { useLandingDoorActions } from "./useLandingDoorActions";
import { useJourneys } from "../JourneyRail/useJourneys";
import { plannerHref } from "../../lib/navPaths";
import "../FG/fg.css";

/**
 * Landing-only variant of the app sub-nav (rendered via `<Page subnav>`). Same
 * full-bleed bar as {@link AppSubNav}, but the tabs are in-page section anchors
 * and the trailing slot is a "Build my fueling plan" CTA (opens the plan wizard
 * — the page's one primary action). Other routes keep the normal `<AppSubNav>`
 * route tabs.
 *
 * When the athlete already has an active race journey, a purple "Go to my
 * training" banner sits under the bar so returning visitors aren't only nudged
 * to start another race.
 *
 * `showSweepstakes` mirrors the same flag on the landing so the "Win free fuel"
 * anchor only appears when the sweepstakes band is actually on the page.
 *
 * On `/faq`, pass `faqActive` so the FAQ tab links to the current page (active)
 * and the other tabs deep-link back to the landing sections.
 */
export function LandingSubNav({
  showSweepstakes = false,
  faqActive = false
}: {
  showSweepstakes?: boolean;
  faqActive?: boolean;
}) {
  const { onApplyPlan } = useLandingDoorActions();
  const { journeys } = useJourneys();
  // Active list: rolling = "just training"; absent journeyType ⇒ race.
  const hasActiveRace = journeys.some((j) => j.journeyType !== "rolling");
  const tabClass = (active: boolean) =>
    `fgg-subnav__tab${active ? " fgg-subnav__tab--active" : ""}`;
  const anchors: { label: string; hash: string; active?: boolean }[] = [
    { label: "How it works", hash: "#how-it-works" },
    { label: "What's in a box", hash: "#whats-in-a-box" },
    { label: "Who it's for", hash: "#who-its-for" },
    ...(showSweepstakes ? [{ label: "Win free fuel", hash: "#win" }] : []),
    { label: "FAQ", hash: "#faq", active: faqActive }
  ];
  return (
    <div className="fgg-subnav-stack">
      <div className="fgg-subnav fgg-subnav--landing">
        <div className="fgg-subnav__inner fg-page-width">
          <nav className="fgg-subnav__nav fgg-subnav__nav--anchors" aria-label="Page sections">
            {anchors.map((a) => {
              const className = tabClass(!!a.active);
              if (faqActive) {
                const to = a.active ? "/faq" : `/landing${a.hash}`;
                return (
                  <Link
                    key={a.hash}
                    to={to}
                    className={className}
                    aria-current={a.active ? "page" : undefined}>
                    {a.label}
                  </Link>
                );
              }
              return (
                <a key={a.hash} href={a.hash} className={className}>
                  {a.label}
                </a>
              );
            })}
          </nav>
          <button
            type="button"
            onClick={onApplyPlan}
            className="fg-btn fg-btn--purple fg-btn--sm fgg-subnav__cta">
            Build my fueling plan →
          </button>
        </div>
      </div>
      {hasActiveRace ? (
        <Link to={plannerHref()} className="fgg-landing-train-banner">
          Go to my training <span className="fgg-landing-train-banner__arrow" aria-hidden>→</span>
        </Link>
      ) : null}
    </div>
  );
}
